import React, { useEffect, useRef, useState } from 'react';
import { StyledLightningAlert } from './style.ts';
import { LightningStatus } from '../EnvironmentInfo';
import { ptwImages } from '../../../../../constants';

const STATUS_LABEL: Record<LightningStatus, string> = {
  emergency: 'Emergency', // 거리 3 이하
  danger: 'Danger', // 거리 10 이하
  warning: 'Warning', // 거리 25 이하
  caution: 'Caution', // 거리 55 이하
  normal: 'Normal'
};

interface IProps {
  data: any;
  onOk?: () => void;
}

export const LightningAlert: React.FC<IProps> = ({ data, onOk }) => {
  const [visible, setVisible] = useState(false);
  const [frozenData, setFrozenData] = useState<any>(null);
  const [acknowledgedAt, setAcknowledgedAt] = useState<number | null>(null);

  const SUPPRESS_MS = 5 * 60 * 1000; // 5분

  const displayData = frozenData ?? data;

  const lightningStatus: LightningStatus = (() => {
    const dist = displayData?.lightningDistKm;
    if (dist == null) return 'normal';
    if (dist <= 3) return 'emergency';
    if (dist <= 10) return 'danger';
    if (dist <= 25) return 'warning';
    if (dist <= 55) return 'caution';
    return 'normal';
  })();
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (data?.lightningDistKm == null || data.lightningDistKm > 55) return;
    const suppressed = acknowledgedAt !== null && Date.now() - acknowledgedAt < SUPPRESS_MS;
    if (!suppressed) {
      setFrozenData(data);
      setVisible(true);
    }
  }, [data]);

  const handleClose = () => {
    setAcknowledgedAt(Date.now());
    setVisible(false);
    setFrozenData(null);
  };
  const handleOpenControl = () => {
    setAcknowledgedAt(Date.now());
    setVisible(false);
    setFrozenData(null);
    onOk?.();
  };

  const handleHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = modalRef.current!.getBoundingClientRect();
    const parentEl = modalRef.current!.offsetParent as HTMLElement | null;
    const parentRect = parentEl ? parentEl.getBoundingClientRect() : { left: 0, top: 0 };
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    const onMouseMove = (ev: MouseEvent) => {
      setPosition({
        x: ev.clientX - dragOffset.current.x - parentRect.left,
        y: ev.clientY - dragOffset.current.y - parentRect.top
      });
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  if (!visible) return null;

  const dragStyle = position ? { left: position.x, top: position.y, transform: 'none' } : undefined;

  return (
    <StyledLightningAlert ref={modalRef} style={dragStyle}>
      <div className={'la-header'} onMouseDown={handleHeaderMouseDown}>
        <span className={'la-title'}>Lightning Alert</span>
        <button
          className={'la-close-btn'}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => { setVisible(false); setFrozenData(null); }}
        >
          <img src={ptwImages.dashboardPopupX} width={25} height={25} alt="close" />
        </button>
      </div>
      <div className={'la-body'}>
        <p className={'la-message'}>
          Lightning has been detected.
          <br />
          Would you like to issue a lightning alert?
        </p>
        <div className={`la-status-card la-${lightningStatus}`}>
          <span className={'la-badge'}>{STATUS_LABEL[lightningStatus]}</span>
          <div className={'la-info'}>
            <div className={'la-info-row'}>
              <span className={'la-info-label'}>Distance :</span>
              <span className={'la-info-value la-highlight'}>{displayData?.lightningDistKm ?? '-'} km</span>
            </div>
            <div className={'la-info-row'}>
              <span className={'la-info-label'}>Real-time (10min) :</span>
              <span className={'la-info-value'}>{String(displayData?.lightningCnt ?? 0)}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={'la-footer'}>
        <button className={'la-btn la-btn-close'} onClick={handleClose}>
          Close
        </button>
        <button className={'la-btn la-btn-ok'} onClick={handleOpenControl}>
          OK
        </button>
      </div>
    </StyledLightningAlert>
  );
};
